// Tutor preview of an assigned diagnostics activity, activity_type 13.
//
// Mirrors the other TutorPreview pages: the tutor app redirects here with its
// own JWT in `?auth=`, that becomes the Bearer token, and the backend checks
// `canTutorAccessStudent`. Nothing on this page writes: no attempt rows, no
// timer, no submit, so a tutor can look through a test without disturbing the
// student's session.
//
// It reads the session's own question rows rather than re-deriving a test from
// year + topics: the assigned rows are fixed at assignment time, and a
// prerequisite test has no year/topic to derive from at all.

import React, { useState, useEffect } from "react"
import { Container } from "reactstrap"
import { useParams, useLocation, useNavigate } from "react-router-dom"
import axios from "axios"
import { MathJax, MathJaxContext } from "better-react-mathjax"
// @ts-ignore - no types shipped
import Loader from "react-spinner-loader"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { BASE_URL } from "../../global"

type PreviewAnswer = {
  answer_id: number
  answer_text: string
  answer_is_correct: number
}

type PreviewQuestion = {
  diagnostic_id: number
  diagnostic_text: string
  display_name: string
  skill_name: string
  question_order: number
  phase: string
  answers: PreviewAnswer[]
}

const LETTERS = ["A", "B", "C", "D", "E", "F"]

// Only BASE questions were part of the original assignment. Anything else was
// earned during the test, so it is labelled rather than passed off as planned.
function phaseLabel(phase: string): string | null {
  if (!phase || phase === "BASE") return null
  if (phase === "FM_RETRY") return "Follow-up question"
  if (phase.startsWith("PREREQ_L")) {
    return `Prerequisite · level ${phase.slice("PREREQ_L".length)}`
  }
  return phase
}

const PreviewBmdDetails = () => {
  document.title = "MyGooRoo Student App"
  const { key } = useParams()
  const navigate = useNavigate()

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const st_key = searchParams.get("st_key") || null
  const auth = searchParams.get("auth") || null

  const [loader, setLoader] = useState<boolean>(false)
  const [questions, setQuestions] = useState<PreviewQuestion[]>([])
  const [isPrereq, setIsPrereq] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!key || !auth) {
      setError("This preview link is missing its activity or sign-in details.")
      return
    }
    setLoader(true)
    axios.defaults.headers.common["Authorization"] = `Bearer ${auth}`
    axios
      .post(BASE_URL + "bmd/previewAssignedActivity", {
        pk_activity_key: key,
        pk_student_key: st_key,
      })
      .then((res) => {
        const data = res.data || {}
        setQuestions(Array.isArray(data.questions) ? data.questions : [])
        setIsPrereq(!!data.is_prereq_test)
        setLoader(false)
      })
      .catch(() => {
        setError("Unable to load this diagnostics preview.")
        setLoader(false)
        toast.error("Unable to load this diagnostics preview.", {
          autoClose: 3000,
        })
      })
  }, [key, auth, st_key])

  const title = isPrereq ? "Prerequisite Test" : "Baseline Math Diagnostics"

  return (
    <React.Fragment>
      <Loader show={loader} type="body" />
      <ToastContainer />
      <div className="page-wrapper">
        <div className="cs-pg-header bg-dark-blue py-4">
          <Container className="custom-wt">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <span
                className="text-white font-size-14 text-uppercase cursor-pointer"
                onClick={() => navigate(-1)}
              >
                <i className="mdi mdi-chevron-left"></i> Back
              </span>
              <h6 className="mb-0 font-size-20 text-white fw-regular">
                {title} — preview
              </h6>
              <span className="text-white font-size-14">
                {questions.length} question{questions.length === 1 ? "" : "s"}
              </span>
            </div>
          </Container>
        </div>

        <Container className="custom-wt">
          {error ? (
            <div className="bg-white rounded shadow-sm p-4 mt-4 text-danger">
              {error}
            </div>
          ) : null}

          {!error && !loader && questions.length === 0 ? (
            <div className="bg-white rounded shadow-sm p-4 mt-4 text-muted">
              This activity has no questions yet.
            </div>
          ) : null}

          <MathJaxContext>
            {questions.map((q, index) => {
              const label = phaseLabel(q.phase)
              return (
                <div
                  key={`${q.diagnostic_id}-${q.question_order}-${index}`}
                  className="bg-white rounded shadow-sm p-4 mt-4"
                >
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                    <div>
                      <div className="fw-bold font-size-16">
                        Question {index + 1}
                      </div>
                      <div className="text-muted font-size-13">
                        {q.display_name}
                        {q.skill_name ? ` · ${q.skill_name}` : ""}
                      </div>
                    </div>
                    {label ? (
                      <span className="badge bg-light text-dark font-size-12">
                        {label}
                      </span>
                    ) : null}
                  </div>

                  <MathJax inline dynamic>
                    <div
                      className="font-size-15 mb-3"
                      dangerouslySetInnerHTML={{ __html: q.diagnostic_text }}
                    />
                    <ul className="list-unstyled mb-0">
                      {q.answers.map((a, i) => {
                        const correct = Number(a.answer_is_correct) === 1
                        return (
                          <li
                            key={a.answer_id}
                            className={`p-3 mb-2 rounded d-flex align-items-start gap-3 ${
                              correct ? "correct-ans" : "border"
                            }`}
                          >
                            <span className="fw-bold">{LETTERS[i] || "-"}.</span>
                            <span
                              className="flex-grow-1"
                              dangerouslySetInnerHTML={{
                                __html: a.answer_text,
                              }}
                            />
                            {correct ? (
                              <span className="badge bg-success">Correct</span>
                            ) : null}
                          </li>
                        )
                      })}
                    </ul>
                  </MathJax>
                </div>
              )
            })}
          </MathJaxContext>
          <div style={{ height: 24 }} />
        </Container>
      </div>
    </React.Fragment>
  )
}

export default PreviewBmdDetails
